import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { FontStyle } from './SysEditor';

interface EditorContentProps {
  content: string;
  placeholder: string;
  height: number;
  fontStyle: FontStyle;
  onContentChange: (content: string) => void;
  onSelectionChange: (selectedText: string, selection: { start: number; end: number } | null) => void;
  disabled?: boolean;
}

const EditorContent = forwardRef<HTMLDivElement, EditorContentProps>(({
  content,
  placeholder,
  height,
  fontStyle,
  onContentChange,
  onSelectionChange,
  disabled = false
}, ref) => {
  const internalRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => internalRef.current!, []);

  useEffect(() => {
    // Só atualiza o DOM se o conteúdo for diferente do que já está lá
    // Isso evita o reset do cursor (que pula para o início) durante a digitação
    if (internalRef.current) {
      const currentHTML = internalRef.current.innerHTML;
      if (currentHTML !== content) {
        // Se a diferença for apenas normalização simples de espaço/vazio ou classes visuais temporárias, não atualizar
        const normalizedCurrent = currentHTML
          .replace(/&nbsp;/g, ' ')
          .replace(/\s*syseditor-selected-cell\s*/g, '')
          .trim();
          
        const normalizedNew = content
          .replace(/&nbsp;/g, ' ')
          .replace(/\s*syseditor-selected-cell\s*/g, '')
          .trim();

        if (normalizedCurrent !== normalizedNew) {
          internalRef.current.innerHTML = content;
        }
      }
    }
  }, [content]);

  const handleInput = () => {
    if (internalRef.current) {
      const newContent = internalRef.current.innerHTML;
      onContentChange(newContent);
    }
  };

  const handleSelectionChange = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const selectedText = selection.toString();
      const range = selection.getRangeAt(0);

      onSelectionChange(selectedText, {
        start: range.startOffset,
        end: range.endOffset
      });
    } else {
      onSelectionChange('', null);
    }
  };

  const handleClick = () => {
    // Detectar formatação no ponto do cursor mesmo sem seleção
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = selection.toString();

      // Sempre chamar onSelectionChange para detectar formatação, mesmo sem texto selecionado
      onSelectionChange(selectedText || ' ', {
        start: range.startOffset,
        end: range.endOffset
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Permitir atalhos de teclado comuns
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'b':
          event.preventDefault();
          document.execCommand('bold');
          handleInput();
          break;
        case 'i':
          event.preventDefault();
          document.execCommand('italic');
          handleInput();
          break;
        case 'u':
          event.preventDefault();
          document.execCommand('underline');
          handleInput();
          break;
        default:
          break;
      }
    }
  };

  const editorStyles = {
    minHeight: height || '100%',
    padding: '3mm',
    border: 'none',
    outline: 'none',
    cursor: disabled ? 'not-allowed' : 'text',
    backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
    color: disabled ? 'text.disabled' : 'text.primary',
    lineHeight: 1.5,
    wordWrap: 'break-word' as const,
    overflowWrap: 'break-word' as const
  };

  return (
    <div
      ref={internalRef}
      contentEditable={!disabled}
      onInput={handleInput}
      onMouseUp={handleSelectionChange}
      onKeyUp={handleSelectionChange}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={editorStyles}
      suppressContentEditableWarning={true}
      data-placeholder={placeholder}
      className="editor-content"
    />
  );
});

EditorContent.displayName = 'EditorContent';

export default EditorContent; 